/**
 * Centralized Rigging Job status and pipeline stage mapping.
 * Maps only real BakeJob / BakeAttempt states — never invents backend enums.
 */

import { getRiggingOperation, isAiRiggingWorkerRuntimeEnabled } from './aiRigging';

/** @typedef {'draft'|'validating'|'ready'|'queued'|'waiting_for_worker'|'claimed'|'running'|'uploading_results'|'awaiting_review'|'succeeded'|'succeeded_with_warnings'|'failed'|'cancelled'|'lease_lost'|'runtime_unavailable'|'blocked'} RiggingDisplayStatus */

const JOB_STATUS_MAP = {
  queued: 'queued',
  running: 'running',
  cancel_requested: 'cancelled',
  succeeded: 'succeeded',
  failed: 'failed',
  canceled: 'cancelled',
};

const ATTEMPT_STATUS_MAP = {
  pending: 'waiting_for_worker',
  claimed: 'claimed',
  preparing: 'running',
  running: 'running',
  uploading: 'uploading_results',
  succeeded: 'succeeded',
  failed: 'failed',
  canceled: 'cancelled',
  timed_out: 'failed',
  lost: 'lease_lost',
};

const DISPLAY_LABELS = {
  draft: 'Draft',
  validating: 'Validating',
  ready: 'Ready',
  queued: 'Queued',
  waiting_for_worker: 'Waiting for Worker',
  claimed: 'Claimed',
  running: 'Running',
  uploading_results: 'Uploading Results',
  awaiting_review: 'Awaiting Review',
  succeeded: 'Succeeded',
  succeeded_with_warnings: 'Succeeded with Warnings',
  failed: 'Failed',
  cancelled: 'Cancelled',
  lease_lost: 'Lease Lost',
  runtime_unavailable: 'Runtime Unavailable',
  blocked: 'Blocked',
};

/**
 * @param {{ status?: string, latest_attempt_status?: string|null, result_status?: string|null, workerRuntimeEnabled?: boolean }} job
 * @returns {RiggingDisplayStatus}
 */
export function mapRiggingJobDisplayStatus(job) {
  const workerRuntimeEnabled =
    job.workerRuntimeEnabled !== undefined
      ? job.workerRuntimeEnabled
      : isAiRiggingWorkerRuntimeEnabled();
  const jobStatus = JOB_STATUS_MAP[job.status] || job.status || 'queued';
  if (job.result_status === 'succeeded_with_warnings') {
    return 'succeeded_with_warnings';
  }
  if (job.result_status === 'succeeded' && jobStatus === 'succeeded') {
    return 'succeeded';
  }
  if (['cancelled', 'failed', 'succeeded'].includes(jobStatus)) {
    return /** @type {RiggingDisplayStatus} */ (jobStatus);
  }
  if (!workerRuntimeEnabled && (jobStatus === 'queued' || jobStatus === 'running')) {
    return 'runtime_unavailable';
  }
  if (job.latest_attempt_status && ATTEMPT_STATUS_MAP[job.latest_attempt_status]) {
    const mapped = ATTEMPT_STATUS_MAP[job.latest_attempt_status];
    if (mapped === 'waiting_for_worker' && jobStatus === 'queued') {
      return workerRuntimeEnabled ? 'waiting_for_worker' : 'runtime_unavailable';
    }
    return mapped;
  }
  if (jobStatus === 'queued') {
    return workerRuntimeEnabled ? 'waiting_for_worker' : 'runtime_unavailable';
  }
  return /** @type {RiggingDisplayStatus} */ (jobStatus);
}

export function riggingJobStatusLabel(displayStatus) {
  return DISPLAY_LABELS[displayStatus] || String(displayStatus || 'Unknown');
}

const STAGE_LABELS = {
  project_memory: 'Project Memory',
  source_revision: 'Source Revision',
  static_validation: 'Static Validation',
  waiting_for_worker: 'Waiting for Worker',
  project_setup: 'Maya Project Setup',
  skeleton: 'Skeleton',
  rig: 'Rig',
  autoskin: 'AutoSkin',
  validation: 'Validation',
  export: 'Export',
  artifacts: 'Artifacts',
  result_review: 'Result Review',
};

/**
 * @returns {{ id: string, label: string, state: string }[]}
 */
export function deriveRiggingPipelineStages({
  operation,
  jobStatus,
  attemptStatus,
  hasArtifacts,
  hasResult,
  workerRuntimeEnabled = isAiRiggingWorkerRuntimeEnabled(),
}) {
  const op = getRiggingOperation(operation);
  const opStages = op ? op.stages : ['validation', 'artifacts', 'result_review'];
  const base = [
    'project_memory',
    'source_revision',
    'static_validation',
    'waiting_for_worker',
    ...opStages,
  ];
  // Deduplicate while preserving order
  const ids = [...new Set(base)];

  const display = mapRiggingJobDisplayStatus({
    status: jobStatus,
    latest_attempt_status: attemptStatus,
    workerRuntimeEnabled,
  });

  return ids.map((id) => {
    let state = 'pending';
    if (['project_memory', 'source_revision', 'static_validation'].includes(id)) {
      state = 'succeeded';
    } else if (id === 'waiting_for_worker') {
      if (!workerRuntimeEnabled) state = 'unavailable';
      else if (['claimed', 'running', 'uploading_results', 'succeeded', 'succeeded_with_warnings'].includes(display)) {
        state = 'succeeded';
      } else if (display === 'waiting_for_worker' || display === 'queued') state = 'waiting';
      else if (display === 'failed' || display === 'cancelled') state = 'blocked';
      else state = 'pending';
    } else if (id === 'artifacts') {
      state = hasArtifacts ? 'succeeded' : display === 'running' ? 'running' : 'pending';
    } else if (id === 'result_review') {
      state = hasResult ? 'succeeded' : 'pending';
    } else if (
      ['autoskin', 'export'].includes(id) &&
      op &&
      !op.availability().available
    ) {
      state = 'unavailable';
    } else if (display === 'running' || display === 'claimed') {
      state = 'running';
    } else if (display === 'succeeded' || display === 'succeeded_with_warnings') {
      state = 'succeeded';
    } else if (display === 'failed') {
      state = 'failed';
    } else if (display === 'cancelled' || display === 'lease_lost') {
      state = 'cancelled';
    } else if (display === 'runtime_unavailable') {
      state = 'unavailable';
    }
    return { id, label: STAGE_LABELS[id] || id, state };
  });
}

export function shortJobId(id) {
  if (!id) return '—';
  const text = String(id);
  return text.length > 8 ? text.slice(0, 8) : text;
}

export function shortHash(hash) {
  if (!hash) return '—';
  const text = String(hash);
  if (text.length <= 12) return text;
  return `${text.slice(0, 8)}…${text.slice(-4)}`;
}
